import ExcelJS from 'exceljs';
import { Namespace } from '@app/Dashboard/models';

/**
 * Prevents spreadsheet formula injection by prefixing strings that start with
 * formula trigger characters (=, +, -, @) with an apostrophe, which Excel and
 * Google Sheets treat as a "force text" escape that is not displayed in the cell.
 */
function sanitizeCell(value: string | undefined | null, fallback = 'N/A'): string {
    if (value == null || value === '') return fallback;
    return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

function formatDuration(ms: number): string {
    if (ms < 0) return 'N/A';
    const totalMinutes = Math.floor(ms / 60000);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function createExcelStyles() {
    const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF2B579A' } },
        alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
        border: {
            top: { style: 'thin' as const, color: { argb: 'FF000000' } },
            left: { style: 'thin' as const, color: { argb: 'FF000000' } },
            bottom: { style: 'thin' as const, color: { argb: 'FF000000' } },
            right: { style: 'thin' as const, color: { argb: 'FF000000' } },
        },
    };

    const evenRowStyle = {
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFFFFFF' } },
        border: {
            top: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
            left: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
            bottom: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
            right: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
        },
    };

    const oddRowStyle = {
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF8F9FA' } },
        border: {
            top: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
            left: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
            bottom: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
            right: { style: 'thin' as const, color: { argb: 'FFE9ECEF' } },
        },
    };

    return { headerStyle, evenRowStyle, oddRowStyle };
}

function applyRowStyles(
    worksheet: ExcelJS.Worksheet,
    startRow: number,
    endRow: number,
    evenRowStyle: ReturnType<typeof createExcelStyles>['evenRowStyle'],
    oddRowStyle: ReturnType<typeof createExcelStyles>['oddRowStyle'],
) {
    for (let rowNum = startRow; rowNum <= endRow; rowNum++) {
        const row = worksheet.getRow(rowNum);
        const style = (rowNum - startRow) % 2 === 1 ? oddRowStyle : evenRowStyle;
        row.eachCell((cell) => {
            cell.fill = style.fill;
            cell.border = style.border;
            cell.alignment = {
                horizontal: typeof cell.value === 'number' ? 'right' : 'left',
                vertical: 'middle',
            };
        });
    }
}

function createNamespacesSummarySheet(
    workbook: ExcelJS.Workbook,
    namespaces: Namespace[],
    periodLabel: string,
    styles: ReturnType<typeof createExcelStyles>,
): void {
    const { headerStyle, evenRowStyle, oddRowStyle } = styles;
    const sheet = workbook.addWorksheet('Namespaces');
    const now = new Date();

    sheet.mergeCells('A1:H1');
    const titleCell = sheet.getCell('A1');
    titleCell.value = `EOD NAMESPACE EXPORT — ${periodLabel.toUpperCase()}`;
    titleCell.font = { bold: true, size: 14, color: { argb: 'FF2B579A' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getRow(1).height = 28;

    sheet.mergeCells('A2:H2');
    const subtitleCell = sheet.getCell('A2');
    subtitleCell.value = `Exported: ${now.toLocaleString()}  |  Namespaces shown: ${namespaces.length}`;
    subtitleCell.font = { italic: true, size: 10, color: { argb: 'FF666666' } };
    subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    const headers = [
        'Namespace',
        'Creator',
        'Team',
        'Class',
        'Status',
        'Last Modified',
        'Time Running',
        'Resources (#)',
    ];
    headers.forEach((header, index) => {
        const cell = sheet.getCell(4, index + 1);
        cell.value = header;
        Object.assign(cell, headerStyle);
    });
    sheet.getRow(4).height = 22;

    namespaces.forEach((ns, rowIndex) => {
        const row = rowIndex + 5;
        const lastModified = ns.date ? new Date(ns.date) : null;
        const timeRunning = lastModified ? formatDuration(now.getTime() - lastModified.getTime()) : 'N/A';

        sheet.getCell(row, 1).value = sanitizeCell(ns.name);
        sheet.getCell(row, 2).value = sanitizeCell(ns.creator);
        sheet.getCell(row, 3).value = sanitizeCell(ns.team);
        sheet.getCell(row, 4).value = sanitizeCell(ns.class);
        sheet.getCell(row, 5).value = sanitizeCell(ns.status);
        sheet.getCell(row, 6).value = lastModified ? lastModified.toLocaleString() : 'N/A';
        sheet.getCell(row, 7).value = timeRunning;
        sheet.getCell(row, 8).value = ns.resources ? ns.resources.length : 0;
    });

    if (namespaces.length > 0) {
        applyRowStyles(sheet, 5, 4 + namespaces.length, evenRowStyle, oddRowStyle);
    }

    sheet.columns = [
        { width: 32 },
        { width: 30 },
        { width: 18 },
        { width: 15 },
        { width: 14 },
        { width: 22 },
        { width: 16 },
        { width: 14 },
    ];
}

function createResourcesDetailSheet(
    workbook: ExcelJS.Workbook,
    namespaces: Namespace[],
    styles: ReturnType<typeof createExcelStyles>,
): void {
    const { headerStyle, evenRowStyle, oddRowStyle } = styles;
    const sheet = workbook.addWorksheet('Resources');

    sheet.mergeCells('A1:H1');
    const titleCell = sheet.getCell('A1');
    titleCell.value = 'RESOURCE DETAILS';
    titleCell.font = { bold: true, size: 14, color: { argb: 'FF2B579A' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getRow(1).height = 28;

    const headers = [
        'Namespace',
        'Resource',
        'Type',
        'Status',
        'Branch',
        'Commit',
        'Deploy Date',
        'Deploy Job',
    ];
    headers.forEach((header, index) => {
        const cell = sheet.getCell(3, index + 1);
        cell.value = header;
        Object.assign(cell, headerStyle);
    });
    sheet.getRow(3).height = 22;

    let row = 4;
    namespaces.forEach((ns) => {
        (ns.resources || []).forEach((resource) => {
            const deployDate = resource.deploy?.date ? new Date(resource.deploy.date).toLocaleString() : 'N/A';
            sheet.getCell(row, 1).value = sanitizeCell(ns.name);
            sheet.getCell(row, 2).value = sanitizeCell(resource.name);
            sheet.getCell(row, 3).value = sanitizeCell(resource.type);
            sheet.getCell(row, 4).value = sanitizeCell(resource.status);
            sheet.getCell(row, 5).value = sanitizeCell(resource.deploy?.branch || resource.git?.branch);
            sheet.getCell(row, 6).value = sanitizeCell(resource.deploy?.commit);
            sheet.getCell(row, 7).value = deployDate;
            sheet.getCell(row, 8).value = sanitizeCell(resource.deploy?.job);
            row++;
        });
    });

    const dataRows = row - 4;
    if (dataRows > 0) {
        applyRowStyles(sheet, 4, 3 + dataRows, evenRowStyle, oddRowStyle);
    }

    sheet.columns = [
        { width: 32 },
        { width: 30 },
        { width: 16 },
        { width: 14 },
        { width: 28 },
        { width: 20 },
        { width: 22 },
        { width: 50 },
    ];
}

export async function exportNamespacesToExcel(namespaces: Namespace[], periodLabel: string): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'EOD Dashboard';
    workbook.created = new Date();

    const styles = createExcelStyles();
    createNamespacesSummarySheet(workbook, namespaces, periodLabel, styles);
    createResourcesDetailSheet(workbook, namespaces, styles);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `namespaces-export-${timestamp}.xlsx`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}
